/* eslint-disable @typescript-eslint/no-deprecated */
import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconBack,
  InformationalScreen,
  LoadingModal,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { type FormikHelpers, useFormik } from 'formik';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { FormContainer, Input, MainContainer } from '~/components/utility';
import { ApiStatus, ErrorCode } from '~/constants';
import { useApiError, useAuth } from '~/hooks';
import { type RootState } from '~/reducers';
import { updateEmail } from '~/services/patient';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  auth: { account_id },
  env: { osType, osVersion, appVersion, deviceId },
}: RootState) => ({
  accountId: account_id,
  deviceId,
  osType,
  osVersion,
  appVersion,
});

const initialValuesFormik = {
  email: '',
  repeatEmail: '',
};

type UpdateEmailFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'update-email';

const UpdateEmail = ({
  accountId,
  osType,
  osVersion,
  appVersion,
  deviceId,
}: Props) => {
  const validationSchema = z
    .object({
      email: z
        .string(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.email.errors.required',
          ),
        )
        .trim()
        .min(
          1,
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.email.errors.required',
          ),
        )
        .email(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.email.errors.emailAddressValidEmail',
          ),
        ),
      repeatEmail: z
        .string(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.required',
          ),
        )
        .trim()
        .min(
          1,
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.required',
          ),
        )
        .email(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.emailAddressValidEmail',
          ),
        ),
    })
    .refine(
      (data) => {
        return data.email === data.repeatEmail;
      },
      {
        path: ['repeatEmail'],
        error: i18n.t(
          'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.emailMustMatch',
        ),
      },
    );

  const handleClickBack = useCallback(() => {
    mediator.publish('router:navigate', '/account-information');
  }, []);

  const { showApiErrorModal, showNetworkErrorModal, showCustomError } =
    useApiError();
  //  Reusing error modals
  const { handleLoginError, showHcpError } = useAuth('changepassword');

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: FormikHelpers<UpdateEmailFormValues>['setFieldValue'],
      setTouched: FormikHelpers<UpdateEmailFormValues>['setTouched'],
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            void setFieldValue('email', '');
            void setFieldValue('repeatEmail', '');
          });
          break;

        case ApiStatus.CONFLICT: {
          void setFieldValue('email', '');
          void setFieldValue('repeatEmail', '');
          showApiErrorModal();
          void setTouched({});
          const errorCode = error.response?.data.code;

          if (errorCode === ApiStatus.PATIENT_EXISTS) {
            showCustomError(i18n.t('Global.modals.emailExists.body'));
          } else {
            showCustomError(error.response?.data.details ?? '');
          }

          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [
      handleLoginError,
      showApiErrorModal,
      showHcpError,
      showNetworkErrorModal,
      showCustomError,
    ],
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdateEmailFormValues,
      {
        setSubmitting,
        resetForm,
        setFieldValue,
        setTouched,
      }: FormikHelpers<UpdateEmailFormValues>,
    ) => {
      try {
        await updateEmail({
          updateEmailData: {
            userID: accountId,
            email: values.email,
          },
          deviceId,
          osType,
          osVersion,
          appVersion,
        });

        notifyApp('adc-webview:update-email');
        mediator.publish('router:navigate', '/account-information');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched,
        );
      }

      setSubmitting(false);
    },
    [accountId, appVersion, osType, osVersion, handleApiRequestError, deviceId],
  );

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
    dirty,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validateOnBlur: true,
    validationSchema: toFormikValidationSchema(validationSchema),
  });

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={{
            title: i18n.t('UpdateEmail.title'),
            buttonStart: {
              element: <IconBack testID="nav-back" />,
              label: 'Go Back',
              onPress: handleClickBack,
            },
            testID: 'nav-back',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              <Input
                aria-label={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.email.label',
                )}
                placeholder={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.email.placeholder',
                )}
                label={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.email.label',
                )}
                errorMessage={errors.email}
                isInvalid={Boolean(errors.email && touched.email)}
                testID={buildTestId({ testID, suffix: ['email', 'input'] })}
                value={values.email}
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                inputMode="email"
              />
              <Input
                aria-label={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.repeatEmail.label',
                )}
                placeholder={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.repeatEmail.placeholder',
                )}
                label={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.repeatEmail.label',
                )}
                errorMessage={errors.repeatEmail}
                isInvalid={Boolean(errors.repeatEmail && touched.repeatEmail)}
                testID={buildTestId({
                  testID,
                  suffix: ['repeat', 'email', 'input'],
                })}
                value={values.repeatEmail}
                onChangeText={handleChange('repeatEmail')}
                onBlur={handleBlur('repeatEmail')}
                inputMode="email"
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={!isValid || isSubmitting || !dirty}
              onPress={() => {
                handleSubmit();
              }}
              testID={buildTestId({ testID, suffix: ['save', 'button'] })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdateEmail);
